import React from 'react';
import { Platform } from 'react-native';

/**
 * SpeakerGrid — Google Meet-style adaptive layout for live-stream tiles.
 * Web-only — wraps raw `<div>` elements that hold Agora-mounted video
 * tracks.
 *
 * Layout rules (revised 2026-05-09 after the «quick room» feedback —
 * users reported that tile[0] was permanently pinned as «primary» so
 * the host occupied 55-100% of the screen and other guests collapsed
 * into a tiny filmstrip; the new layout treats every tile as equal,
 * the way Google Meet's tile view does):
 *
 *   1 speaker   → full-screen single tile
 *   2 speakers  → 50/50 side-by-side
 *   3-4 speakers→ 2×2 grid (one empty cell on 3, all four equal on 4)
 *   5-6 speakers→ 3×2 grid
 *   7-9 speakers→ 3×3 grid
 *   10-12       → 4×3 grid
 *   13-16       → 4×4 grid
 *   17-25       → 5×N grid
 *   26+         → ceil(sqrt(n)) columns, auto rows
 *
 * Caller decides ordering — typically the host (master) first, guests
 * follow. The host is no longer visually pinned: in the new layout
 * tile[0] is simply the top-left cell, same size as every other cell.
 * Caller is also responsible for filtering tiles whose audio AND video
 * are both off (spec D-6 — `shouldRenderCohostTile`).
 *
 * Why no «primary» pin: the prior layout assumed a strict «host + 1
 * guest» interview format and made the host visually dominant in 3/5
 * arrangements. Quick rooms are conversational — every participant is
 * an equal speaker. Pinning created two problems:
 *   1. The host's tile took 55-60% of vertical real estate, leaving
 *      cohort guests in a thin filmstrip that on smaller viewports
 *      could clip below the toolbar.
 *   2. With ≥6 participants the layout fell back to a generic grid
 *      anyway, so the «primary» concept was inconsistent across
 *      participant counts.
 * Equal tiles solve both at once.
 */

export interface SpeakerGridProps {
  /**
   * Pre-rendered tiles in display order. Each tile should be a self-
   * contained block that fills `width: 100%; height: 100%` of its grid
   * cell — the grid only positions, it doesn't size internally.
   */
  tiles: React.ReactNode[];
  /** Outer container gap in px. Defaults to 8. */
  gap?: number;
  /** Outer container padding in px. Defaults to 8. */
  padding?: number;
}

/**
 * Pick the column count that keeps tiles closest to a 16:9 aspect
 * ratio inside a typical landscape container, while ensuring all
 * participants fit on screen. Mirrors Google Meet's defaults.
 */
function colsForCount(n: number): number {
  if (n <= 2) return n;     // 1 → 1 col, 2 → 2 cols
  if (n <= 4) return 2;     // 3-4 → 2×2 (3rd row leaves one empty cell)
  if (n <= 9) return 3;     // 5-6 → 3×2, 7-9 → 3×3
  if (n <= 16) return 4;    // 10-12 → 4×3, 13-16 → 4×4
  if (n <= 25) return 5;    // 17-20 → 5×4, 21-25 → 5×5
  return Math.ceil(Math.sqrt(n));
}

export const SpeakerGrid: React.FC<SpeakerGridProps> = ({ tiles, gap = 8, padding = 8 }) => {
  if (Platform.OS !== 'web') return null;
  const count = tiles.length;
  if (count === 0) return null;

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    padding,
    boxSizing: 'border-box',
    position: 'relative',
  };

  if (count === 1) {
    return (
      <div style={containerStyle}>
        <div style={{ width: '100%', height: '100%' }}>{tiles[0]}</div>
      </div>
    );
  }

  const cols = colsForCount(count);
  const rows = Math.ceil(count / cols);
  // Items in the trailing row when it isn't fully filled. We center
  // them horizontally so an odd count (3, 5, 7…) reads visually
  // balanced instead of sticking the orphan tiles to the left edge.
  // Only applied when the offset is an integer number of columns —
  // sub-column shifts (e.g. 5 tiles in a 3-col layout, last row has 2)
  // are left flush-left because half-column offsets can't be
  // expressed cleanly without spanning tricks.
  const itemsInLastRow = count - (rows - 1) * cols;
  const lastRowGap = cols - itemsInLastRow;
  const canCenterLastRow = lastRowGap > 0 && lastRowGap % 2 === 0;
  const lastRowColStart = canCenterLastRow ? lastRowGap / 2 + 1 : 1;

  return (
    <div
      style={{
        ...containerStyle,
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap,
      }}
    >
      {tiles.map((tile, i) => {
        const inLastRow = i >= (rows - 1) * cols;
        const indexInLastRow = i - (rows - 1) * cols;
        const colStart = inLastRow && canCenterLastRow
          ? lastRowColStart + indexInLastRow
          : undefined;
        return (
          <div
            key={i}
            style={{
              minHeight: 0,
              minWidth: 0,
              ...(colStart != null ? { gridColumnStart: colStart } : null),
            }}
          >
            {tile}
          </div>
        );
      })}
    </div>
  );
};
